import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, Grid, media, Stack, xcss } from '@atlaskit/primitives';

import { JiraOverlayTemplate } from '@/components/signup/layouts/product-overlay-template';

import { ufoEventRedirectToProduct } from '../../../../../../../../components/signup/utils/ufo-events';
import { useCloudStatus } from '../../hooks/use-cloud-status';
import { useCloudStatusRedirection } from '../../hooks/use-cloud-status-redirection';
import { useCreateRedirectUrlWithWizard } from '../onboarding-wizard/utils/create-redirect-url';

import { SetupProps, StepContext } from './types';

const BATCH_ANALYTICS_INTERVAL = 100;

export const Setup = <T extends StepContext>({
	screens,
	initialContext,
	initialScreenIndex,
}: SetupProps<T>) => {
	const { cloudId: tenantId } = useCloudStatus();

	const { resolveBlockingRedirectPromises, createRedirectUrl } = useCloudStatusRedirection();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const createRedirectUrlWithWizard = useCreateRedirectUrlWithWizard();

	const [screenIndex, setScreenIndex] = useState(initialScreenIndex ?? 0);

	const [context, setContext] = useState<T>(() => initialContext ?? ({ tenantId } as T));

	/**
	 * Parent `initialContext.spaceName` can update after async CGS load (e.g. team default → CGS
	 * project name) while `useState` above still holds the first snapshot. Sync when the prop
	 * value changes; skip when unchanged so Space Details edits are not overwritten.
	 */
	const previousInitialSpaceNameRef = useRef<string | undefined>(undefined);

	const currentStep = useMemo(() => screens[screenIndex], [screenIndex, screens]);

	useEffect(() => {
		setContext((prevContext) => ({
			...prevContext,
			tenantId,
		}));
	}, [tenantId]);

	useEffect(() => {
		if (initialContext?.previewCardData) {
			setContext((prevContext) => ({
				...prevContext,
				previewCardData: initialContext.previewCardData,
			}));
		}
	}, [initialContext?.previewCardData]);

	useEffect(() => {
		if (initialContext?.spaceName === undefined) {
			return;
		}
		if (previousInitialSpaceNameRef.current === initialContext.spaceName) {
			return;
		}
		previousInitialSpaceNameRef.current = initialContext.spaceName;

		setContext((prevContext) => ({
			...prevContext,
			spaceName: initialContext.spaceName,
		}));
	}, [initialContext?.spaceName]);

	// TODO: Make onComplete a prop to Setup component to provide more felxibility for other
	// product flows. Currently this is Jira specific logic.
	const onComplete = useCallback(
		async (taskId?: string, onRedirect?: () => void) => {
			const productUrl = createRedirectUrl();

			if (productUrl) {
				await resolveBlockingRedirectPromises();

				if (onRedirect) {
					try {
						onRedirect();
					} catch (error) {
						// NO-OP, prevent redirect from failing
					}
				}

				createAnalyticsEvent({
					eventName: 'redirectedToProduct',
					eventComponent: 'confirmationPage',
					event: 'redirected',
					eventType: 'ui',
					action: 'redirected',
					attributes: {
						reactComponent: 'JiraProvisioningWithOnboardingWizard',
					},
				}).fire();

				// SLO "good event" for redirect into product
				ufoEventRedirectToProduct.success({
					customData: {
						analyticsEvent: 'redirected',
						redirectFrom: 'confirmationPage',
						reactComponent: 'JiraProvisioningWithOnboardingWizard',
						finalProvisioningScreen: 'jira-onboarding-wizard',
					},
				});

				setTimeout(
					() => window.location.assign(createRedirectUrlWithWizard(productUrl, taskId)),
					BATCH_ANALYTICS_INTERVAL,
				);
			}
		},
		[
			createAnalyticsEvent,
			createRedirectUrl,
			createRedirectUrlWithWizard,
			resolveBlockingRedirectPromises,
		],
	);

	const nextStep = useCallback(() => {
		if (screenIndex < screens.length - 1) {
			setScreenIndex(screenIndex + 1);
		} else {
			onComplete(context?.taskId);
		}
	}, [context, screenIndex, screens.length, onComplete]);

	return (
		<JiraOverlayTemplate size="lg" header={''} subheader={''}>
			{currentStep && currentStep.type === 'full' && (
				<currentStep.ScreenComponent
					context={context}
					setContext={setContext}
					nextStep={nextStep}
					{...currentStep}
				/>
			)}
			{currentStep && currentStep.type === 'split' && (
				<Grid xcss={gridStyles} columnGap="space.0" rowGap="space.0">
					<Box xcss={wrapperStyles}>
						<Stack spread="space-between" xcss={stackStyles}>
							<currentStep.LeftComponent
								context={context}
								setContext={setContext}
								nextStep={nextStep}
								{...currentStep}
							/>
						</Stack>
					</Box>
					<Box xcss={wrapperStyles}>
						<currentStep.RightComponent
							context={context}
							setContext={setContext}
							nextStep={nextStep}
							{...currentStep}
						/>
					</Box>
				</Grid>
			)}
		</JiraOverlayTemplate>
	);
};

const gridStyles = xcss({
	width: '100%',
	height: '100%',
	gridTemplateColumns: '100%',
	justifyContent: 'center',
	[media.above.md]: {
		gridTemplateColumns: '50% 50%',
		justifyContent: 'auto',
	},
});

const wrapperStyles = xcss({
	backgroundColor: 'elevation.surface',
	gap: 'space.025',
});

const stackStyles = xcss({
	height: '100%',
	paddingInlineEnd: 'space.0',
	[media.above.md]: {
		paddingInlineEnd: 'space.800',
	},
});
